/**
 * Lazy CDN script/stylesheet loader with dedupe by URL.
 * Used to keep non-critical vendors (e.g. Flatpickr) off the funil first-paint path.
 */

const scriptPromises = new Map();
const stylesheetPromises = new Map();

export const FLATPICKR_CSS =
  "https://cdn.jsdelivr.net/npm/flatpickr@4.6.13/dist/flatpickr.min.css";
const FLATPICKR_JS = "https://cdn.jsdelivr.net/npm/flatpickr@4.6.13";
const FLATPICKR_PT = "https://cdn.jsdelivr.net/npm/flatpickr@4.6.13/dist/l10n/pt.js";

/**
 * Wait until an already-present script finishes loading (or is already complete).
 * @param {HTMLScriptElement} script
 * @returns {Promise<void>}
 */
function waitForExistingScript(script) {
  if (!script) return Promise.resolve();
  if (script.dataset?.aurisCdnLoaded === "1") {
    return Promise.resolve();
  }
  const readyState = /** @type {HTMLScriptElement & { readyState?: string }} */ (script).readyState;
  if (readyState === "complete" || readyState === "loaded") {
    if (script.dataset) script.dataset.aurisCdnLoaded = "1";
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const onLoad = () => {
      cleanup();
      if (script.dataset) script.dataset.aurisCdnLoaded = "1";
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error(`Failed to load script: ${script.src}`));
    };
    const cleanup = () => {
      script.removeEventListener("load", onLoad);
      script.removeEventListener("error", onError);
    };
    script.addEventListener("load", onLoad);
    script.addEventListener("error", onError);
  });
}

/**
 * @param {string} src
 * @returns {Promise<void>}
 */
export function ensureScript(src) {
  if (!src) {
    return Promise.reject(new Error("ensureScript: src is required"));
  }

  const existing = scriptPromises.get(src);
  if (existing) return existing;

  const alreadyInDom = document.querySelector(`script[src="${src}"]`);
  if (alreadyInDom) {
    const resolved = waitForExistingScript(/** @type {HTMLScriptElement} */ (alreadyInDom));
    scriptPromises.set(src, resolved);
    return resolved;
  }

  const promise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.dataset.aurisCdnLoaded = "1";
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });

  scriptPromises.set(src, promise);
  return promise;
}

/**
 * @param {string} href
 * @returns {Promise<void>}
 */
export function ensureStylesheet(href) {
  if (!href) {
    return Promise.reject(new Error("ensureStylesheet: href is required"));
  }

  const existing = stylesheetPromises.get(href);
  if (existing) return existing;

  const alreadyInDom = document.querySelector(`link[rel="stylesheet"][href="${href}"]`);
  if (alreadyInDom) {
    const resolved = Promise.resolve();
    stylesheetPromises.set(href, resolved);
    return resolved;
  }

  const promise = new Promise((resolve, reject) => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`Failed to load stylesheet: ${href}`));
    document.head.appendChild(link);
  });

  stylesheetPromises.set(href, promise);
  return promise;
}

/**
 * Ensure a stylesheet exists on an arbitrary document (e.g. Document PiP).
 * Does not share the opener cache key — each document is independent.
 * @param {string} href
 * @param {Document} targetDocument
 * @returns {Promise<void>}
 */
export function ensureStylesheetInDocument(href, targetDocument) {
  if (!href) {
    return Promise.reject(new Error("ensureStylesheetInDocument: href is required"));
  }
  if (!targetDocument?.head) {
    return Promise.resolve();
  }
  if (targetDocument.querySelector(`link[rel="stylesheet"][href="${href}"]`)) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const link = targetDocument.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`Failed to load stylesheet: ${href}`));
    targetDocument.head.appendChild(link);
  });
}

/**
 * Ensure a script exists on an arbitrary document (e.g. Document PiP).
 * Executes in that document's browsing context so globals bind to its window.
 * @param {string} src
 * @param {Document} targetDocument
 * @returns {Promise<void>}
 */
export function ensureScriptInDocument(src, targetDocument) {
  if (!src) {
    return Promise.reject(new Error("ensureScriptInDocument: src is required"));
  }
  if (!targetDocument?.head) {
    return Promise.resolve();
  }
  const existing = targetDocument.querySelector(`script[src="${src}"]`);
  if (existing) {
    return waitForExistingScript(/** @type {HTMLScriptElement} */ (existing));
  }
  return new Promise((resolve, reject) => {
    const script = targetDocument.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.dataset.aurisCdnLoaded = "1";
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    targetDocument.head.appendChild(script);
  });
}

/**
 * Load Flatpickr CSS + core + Portuguese locale once.
 * Resolves when `window.flatpickr` is a function AND the locale pack has been requested.
 * When core is already present, still ensures the l10n script (deduped).
 * @returns {Promise<void>}
 */
export async function ensureFlatpickr() {
  const coreReady = typeof window.flatpickr === "function";

  if (!coreReady) {
    await ensureStylesheet(FLATPICKR_CSS);
    await ensureScript(FLATPICKR_JS);
  }

  await ensureScript(FLATPICKR_PT);

  if (typeof window.flatpickr !== "function") {
    throw new Error("Flatpickr loaded but window.flatpickr is not a function");
  }
}

/**
 * Ensure Flatpickr is available on the window that owns `targetDocument`
 * (Document PiP / popup). Falls back to opener `ensureFlatpickr` when the
 * target view is the same as the opener.
 * @param {Document} targetDocument
 * @returns {Promise<((el: HTMLElement, opts?: object) => object) | null>}
 */
export async function ensureFlatpickrInDocument(targetDocument) {
  const view = targetDocument?.defaultView;
  if (!view) return null;

  if (view === window) {
    await ensureFlatpickr();
    return typeof window.flatpickr === "function" ? window.flatpickr : null;
  }

  try {
    await ensureStylesheetInDocument(FLATPICKR_CSS, targetDocument);
  } catch {
    /* calendar still usable without mirrored CSS */
  }

  if (typeof view.flatpickr !== "function") {
    await ensureScriptInDocument(FLATPICKR_JS, targetDocument);
  }
  await ensureScriptInDocument(FLATPICKR_PT, targetDocument);

  if (typeof view.flatpickr !== "function") {
    // Last resort: use opener flatpickr with static calendar in the target doc.
    await ensureFlatpickr();
    return typeof window.flatpickr === "function" ? window.flatpickr : null;
  }
  return view.flatpickr;
}
